import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { RefBadge } from "../../components/neu/RefBadge";
import * as api from "../../api/timeOff";

const Actions = ({ t, idYes, idNo, onYes, onNo }) => (
  <div className="mt-4 flex gap-2">
    <NeuButton variant="primary" className="flex items-center gap-2" data-testid={idYes} onClick={onYes}>
      <Check className="h-4 w-4" /> {t("approvals.approve")}
    </NeuButton>
    <NeuButton variant="danger" className="flex items-center gap-2" data-testid={idNo} onClick={onNo}>
      <X className="h-4 w-4" /> {t("approvals.reject")}
    </NeuButton>
  </div>
);

const Meta = ({ t, it }) => (
  <>
    <p className="mt-1 text-xs text-muted-foreground">{t(`timeOff.type.${it.time_off_type}`)} · {it.hours}h</p>
    <p className="mt-2 font-mono text-xs text-muted-foreground">{it.request_date} · {it.start_time || "—"} → {it.end_time || "—"}</p>
    {it.reason ? <p className="mt-2 text-xs text-muted-foreground">“{it.reason}”</p> : null}
  </>
);

// موافقات المدير على الإجازة الزمنية: قيد المراجعة + طلبات إلغائها (استئناف).
export const TimeOffApprovals = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [cancels, setCancels] = useState([]);

  const load = useCallback(() => {
    api.listRequests("pending").then(setItems).catch(() => setItems([]));
    api.listRequests("cancel_requested").then(setCancels).catch(() => setCancels([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const done = () => { window.dispatchEvent(new Event("helcoo:approvals-refresh")); load(); };
  const decide = async (id, status) => {
    try { await api.action(id, status); toast.success(t(`approvals.${status === "approved" ? "approve" : "reject"}d`)); done(); }
    catch { toast.error(t("errors.generic")); }
  };
  const decideCancel = async (id, approve) => {
    try { await api.approveCancel(id, approve, null); toast.success(t(`approvals.${approve ? "approve" : "reject"}d`)); done(); }
    catch { toast.error(t("errors.generic")); }
  };

  if (!items.length && !cancels.length) return null;
  return (
    <div className="grid gap-4" data-testid="time-off-approvals">
      {items.length ? <p className="label-xs">{t("timeOff.pendingTitle", { count: items.length })}</p> : null}
      {items.length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((it) => (
            <NeuCard key={it.id} className="animate-rise-in p-5" data-testid={`time-off-approval-${it.id}`}>
              <p className="text-sm font-black">{it.user_name || it.user_id}</p>
              <RefBadge value={it.ref_no} className="mt-1" />
              <Meta t={t} it={it} />
              <Actions t={t} idYes={`time-off-approve-${it.id}`} idNo={`time-off-reject-${it.id}`}
                       onYes={() => decide(it.id, "approved")} onNo={() => decide(it.id, "rejected")} />
            </NeuCard>
          ))}
        </div>
      ) : null}
      {cancels.length ? <p className="label-xs">{t("timeOff.cancelTitle", { count: cancels.length })}</p> : null}
      {cancels.length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {cancels.map((it) => (
            <NeuCard key={it.id} className="animate-rise-in p-5" data-testid={`time-off-cancel-approval-${it.id}`}>
              <span className="inline-block rounded-full bg-rose-500 px-2.5 py-0.5 text-xs font-black text-white">{t("timeOff.cancelBadge")}</span>
              <p className="mt-2 text-sm font-black">{it.user_name || it.user_id}</p>
              <RefBadge value={it.ref_no} className="mt-1" />
              <Meta t={t} it={it} />
              <Actions t={t} idYes={`time-off-cancel-approve-${it.id}`} idNo={`time-off-cancel-reject-${it.id}`}
                       onYes={() => decideCancel(it.id, true)} onNo={() => decideCancel(it.id, false)} />
            </NeuCard>
          ))}
        </div>
      ) : null}
    </div>
  );
};
